/**
 * Leaflet canvas for the fan viewer — extracted from BrowseStateView
 * during the MVP three-mode rebuild.
 *
 * Changes vs the v1 extraction:
 *   - Pins are DIRECTIONAL ARROWS (L.divIcon replicating the mockup's
 *     .mp markup; rotation via the `--hdg` CSS var, color via the
 *     shared stream-health severity scale from lag.ts). The followed
 *     car gets a ping ring + bigger glyph.
 *   - `resizeKey` triggers map.invalidateSize() after the detail-mode
 *     rail shrinks/expands the canvas (Leaflet only tracks WINDOW
 *     resizes on its own).
 *
 * Everything else (gates, raceline, device trail + playhead seek,
 * fit-bounds-per-selection) is preserved verbatim — load-bearing.
 */

import React, { useEffect, useMemo, useRef } from 'react';

import { EventRow, StageRouteGeometry } from '../../services/fleet';
import { GpsSummary } from '../../types';
import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import DeviceTrailOverlay from '../DeviceTrailOverlay';
import RacelineOverlay from '../RacelineOverlay';
import StageRoutesOverlay from '../StageRoutesOverlay';
import BaseLayers from './BaseLayers';
import { displayCarNumber } from './carNumber';
import { LagInfo, SEVERITY_CSS_COLOR } from './lag';
import './viewer.css';

type LeafletModule = typeof import('leaflet');
type ReactLeafletModule = typeof import('react-leaflet');

export interface MapModules {
  L: LeafletModule;
  RL: ReactLeafletModule;
}

let mapModulesPromise: Promise<MapModules> | null = null;

export async function loadMapModules(): Promise<MapModules> {
  if (!mapModulesPromise) {
    mapModulesPromise = (async () => {
      const [L, RL] = await Promise.all([
        import('leaflet'),
        import('react-leaflet'),
      ]);
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      return { L, RL };
    })();
  }
  return mapModulesPromise;
}

// Directional pin glyph — 1:1 with the mockup's PIN_SVG (viewer.html),
// fills inlined so the HTML string needs no extra CSS classes.
const PIN_SVG =
  '<svg viewBox="0 0 40 40" preserveAspectRatio="xMidYMid meet">' +
  '<circle cx="20" cy="20" r="13" opacity=".45" fill="rgba(0,0,0,.5)"></circle>' +
  '<path fill="currentColor" d="M9 21 A11 11 0 0 1 31 21 L28 21 A8 8 0 0 0 12 21 Z"></path>' +
  '<path fill="currentColor" d="M20 4 L25 12 L15 12 Z"></path>' +
  '<circle cx="20" cy="22" r="8" fill="#0d0d14" stroke="currentColor" stroke-width="2"></circle>' +
  '<circle cx="20" cy="22" r="3.2" fill="#fff"></circle>' +
  '<path fill="rgba(255,255,255,.45)" d="M11 19 A9 9 0 0 1 29 19 L27 19 A7 7 0 0 0 13 19 Z" opacity=".5"></path>' +
  '</svg>';

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Pin icon size/anchor, one rule for both: a ringed pin (selected or
 *  followed) is bigger, and the anchor always stays the icon's center —
 *  it must match CSS's own `ringed` state or the pin sits off the trail. */
export function pinIconGeometry(ringed: boolean): {
  size: number;
  iconSize: [number, number];
  iconAnchor: [number, number];
} {
  const size = ringed ? 44 : 34;
  return { size, iconSize: [size, size], iconAnchor: [size / 2, size / 2] };
}

/** One directional arrow marker per device. Icon rebuilt only when its
 *  visual inputs change (heading / severity / selection / labels). */
const DevicePin: React.FC<{
  modules: MapModules;
  device: EventLiveDevice;
  lag: LagInfo;
  isSelected: boolean;
  isFollowed: boolean;
  onSelect: (id: string) => void;
}> = ({ modules, device, lag, isSelected, isFollowed, onSelect }) => {
  const { Marker } = modules.RL as any;
  const heading = Math.round(device.heading_deg ?? 0);
  const name = device.driver_name || device.device_id;
  const carNumber = displayCarNumber(device);

  const icon = useMemo(() => {
    const color = SEVERITY_CSS_COLOR[lag.severity];
    const ringed = isSelected || isFollowed;
    const html =
      `<div class="tv-mp ${lag.severity}${ringed ? ' you' : ''}" ` +
      `style="--hdg:${heading}deg;color:${color}">` +
      (ringed ? '<div class="tv-mp-ring"></div>' : '') +
      `<div class="tv-mp-arrow">${PIN_SVG}</div>` +
      // Operator-authored, unlike the numeric enrollment id it can fall
      // back to — escape it same as the name/lag.
      `<div class="tv-mp-card"><span class="num">${escapeHtml(carNumber)}</span>` +
      `<span class="nm">${escapeHtml(name)}</span>` +
      `<span class="lag">${escapeHtml(lag.label)}</span></div>` +
      '</div>';
    const { iconSize, iconAnchor } = pinIconGeometry(ringed);
    return modules.L.divIcon({
      html,
      className: 'tv-mp-icon',
      iconSize,
      iconAnchor,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules.L, heading, lag.severity, lag.label, isSelected, isFollowed, carNumber, name]);

  return (
    <Marker
      position={[device.lat, device.lon]}
      icon={icon}
      zIndexOffset={isSelected ? 1000 : 0}
      eventHandlers={{ click: () => onSelect(device.device_id) }}
    />
  );
};

/** Auto-pan to the followed+selected car as it moves. Pan-only (no
 *  zoom change), and only for the car the user explicitly follows —
 *  browsing the map is never hijacked by unfollowed cars. */
const FollowPan: React.FC<{
  useMap: () => any;
  device: { lat: number; lon: number } | null;
}> = ({ useMap, device }) => {
  const map = useMap();
  const lat = device?.lat;
  const lon = device?.lon;
  React.useEffect(() => {
    if (lat == null || lon == null) return;
    map.panTo([lat, lon], { animate: true, duration: 0.6 });
  }, [map, lat, lon]);
  return null;
};

/** Leaflet doesn't observe container resizes — kick it whenever the
 *  detail rail changes the canvas width (waits out the CSS transition). */
const MapResizer: React.FC<{ useMap: any; resizeKey: string }> = ({ useMap, resizeKey }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const t = window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch {
        /* leaflet not ready */
      }
    }, 380); // just past the 350ms canvas transition
    return () => window.clearTimeout(t);
  }, [map, resizeKey]);
  return null;
};

export const MapCanvas: React.FC<{
  modules: MapModules;
  center: [number, number];
  event: EventRow;
  devices: EventLiveDevice[];
  replay?: boolean;
  selectedId: string | null;
  /** Followed cars (ping ring; the SELECTED followed car auto-pans). */
  followedIds?: Set<string>;
  onSelect: (id: string) => void;
  /** Stream-health chip per device — computed by the orchestrator
   *  (player PDT lag for the playing camera, SSE telemetry age for the
   *  rest; see lag.ts). */
  lagFor: (device: EventLiveDevice) => LagInfo;
  trailTrack: GpsSummary['track'] | null;
  trailCoverage: Array<[number, number]> | null;
  playheadUtc: number | null;
  onSeek: (utcMs: number) => void;
  racelineCoords: Array<[number, number]> | null;
  /** Changes when the canvas width changes (mode switch) → invalidateSize. */
  resizeKey: string;
  /** StageStrip selection — that stage's gates draw instead of stage
   *  0's. Null/undefined = All (stage-0 gates, the historic default). */
  selectedStage?: number | null;
  /** Official corridor geometry for every stage (W8) — drawn under the
   *  competitor trail, clicking a route callout selects that stage. */
  stageRoutes?: StageRouteGeometry[];
  onSelectStage?: (stageIndex: number) => void;
}> = ({
  modules,
  center,
  event,
  devices,
  selectedId,
  followedIds,
  onSelect,
  lagFor,
  trailTrack,
  trailCoverage,
  playheadUtc,
  onSeek,
  racelineCoords,
  resizeKey,
  selectedStage = null,
  stageRoutes = [],
  onSelectStage,
}) => {
  const { MapContainer, Circle, Polyline, Pane, useMap } = modules.RL as any;
  return (
    <MapContainer
      center={center}
      zoom={devices.some((d) => d.has_fix) ? 13 : 11}
      scrollWheelZoom
      // Scopes the dark-glass restyle of Leaflet's stock white controls
      // (zoom / layers / attribution) to the fan map — viewer.css.
      className="tv-viewer-map"
      style={{ height: '100%', width: '100%' }}
    >
      <BaseLayers modules={modules} />
      <Pane name="official-stage-routes" style={{ zIndex: 380 }}>
        <StageRoutesOverlay
          L={modules.L}
          RL={modules.RL}
          routes={stageRoutes}
          selectedStage={selectedStage}
          pane="official-stage-routes"
          onSelectStage={onSelectStage ?? (() => {})}
        />
      </Pane>
      {racelineCoords && racelineCoords.length > 1 && (
        <RacelineOverlay modules={modules} coords={racelineCoords} />
      )}
      {/* Gates for ONE stage: the StageStrip selection when active,
          else stage 0 (the historic public default — handover §4.2:
          extra/draft stages never appear unfiltered). */}
      {event.gates.filter((g) => g.stage_index === (selectedStage ?? 0)).map((g) =>
        g.kind === 'lap_line' ? (
          g.lat2 != null && g.lon2 != null && (
            <Polyline
              key={g.id}
              positions={[
                [g.lat, g.lon],
                [g.lat2, g.lon2],
              ]}
              pathOptions={{ color: '#f87171', weight: 3, dashArray: '6 4' }}
            />
          )
        ) : (
          <Circle
            key={g.id}
            center={[g.lat, g.lon]}
            radius={g.radius_m}
            pathOptions={{
              color: g.kind === 'start' ? '#34d399' : '#f87171',
              fillColor: g.kind === 'start' ? '#34d399' : '#f87171',
              fillOpacity: 0.08,
              weight: 2,
              dashArray: '4 4',
            }}
          />
        ),
      )}
      {/* A car with no fix holds its rail row but has no position to
          pin — `lat`/`lon` are 0 there, i.e. the Gulf of Guinea. */}
      {devices.filter((d) => d.has_fix).map((d) => (
        <DevicePin
          key={d.device_id}
          modules={modules}
          device={d}
          lag={lagFor(d)}
          isSelected={d.device_id === selectedId}
          isFollowed={followedIds?.has(d.device_id) ?? false}
          onSelect={onSelect}
        />
      ))}
      {trailTrack && trailTrack.length > 1 && (
        <DeviceTrailOverlay
          modules={modules}
          track={trailTrack}
          coverage={trailCoverage ?? []}
          currentUtcMs={playheadUtc}
          onSeek={onSeek}
        />
      )}
      {trailTrack && trailTrack.length > 1 && (
        <FitBoundsToTrack track={trailTrack} useMap={useMap} selectedId={selectedId} />
      )}
      <FollowPan
        useMap={useMap}
        device={
          selectedId != null && followedIds?.has(selectedId)
            ? devices.find((d) => d.device_id === selectedId && d.has_fix) ?? null
            : null
        }
      />
      <MapResizer useMap={useMap} resizeKey={resizeKey} />
    </MapContainer>
  );
};

// Auto-fit the map to the selected device's full track once per selection.
const FitBoundsToTrack: React.FC<{
  track: GpsSummary['track'];
  useMap: any;
  selectedId: string | null;
}> = ({ track, useMap, selectedId }) => {
  const map = useMap();
  const lastFitForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!map || track.length < 2) return;
    if (lastFitForRef.current === selectedId) return;
    let minLat = track[0][0];
    let maxLat = track[0][0];
    let minLon = track[0][1];
    let maxLon = track[0][1];
    for (const [la, lo] of track) {
      if (la < minLat) minLat = la;
      if (la > maxLat) maxLat = la;
      if (lo < minLon) minLon = lo;
      if (lo > maxLon) maxLon = lo;
    }
    try {
      map.fitBounds(
        [
          [minLat, minLon],
          [maxLat, maxLon],
        ],
        { padding: [40, 40], maxZoom: 17 },
      );
      lastFitForRef.current = selectedId;
    } catch {
      /* leaflet not ready */
    }
    // Deliberately keyed on selectedId — we want one fit per pin click, not
    // a refit on every live-sample extension of `track`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selectedId]);
  return null;
};

export default MapCanvas;
